import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { loadOidcConfig } from './config.js';

interface AuthCtx {
  ready: boolean;
  enabled: boolean;
  user: User | null;
  signin: () => Promise<void>;
  signout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const config = useMemo(loadOidcConfig, []);
  const managerRef = useRef<UserManager | null>(null);
  const [ready, setReady] = useState(!config.enabled);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    if (!config.enabled) return;

    const manager = new UserManager({
      authority: config.issuer,
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      post_logout_redirect_uri: config.postLogoutRedirectUri,
      response_type: 'code',
      scope: config.scope,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      loadUserInfo: true,
      automaticSilentRenew: true,
    });
    managerRef.current = manager;

    const isCallback = window.location.pathname === '/auth/callback';

    (async () => {
      try {
        if (isCallback) {
          const u = await manager.signinCallback();
          setUser(u ?? null);
          // Strip the callback path from the URL so refreshes don't re-run it.
          window.history.replaceState({}, '', '/');
        } else {
          const u = await manager.getUser();
          setUser(u && !u.expired ? u : null);
        }
      } catch (e) {
        console.error('OIDC init failed', e);
        setUser(null);
      } finally {
        setReady(true);
      }
    })();

    const onLoaded = (u: User) => setUser(u);
    const onUnloaded = () => setUser(null);
    manager.events.addUserLoaded(onLoaded);
    manager.events.addUserUnloaded(onUnloaded);
    return () => {
      manager.events.removeUserLoaded(onLoaded);
      manager.events.removeUserUnloaded(onUnloaded);
    };
  }, [config]);

  const signin = useCallback(async () => {
    if (!managerRef.current) return;
    await managerRef.current.signinRedirect();
  }, []);

  const signout = useCallback(async () => {
    if (!managerRef.current) return;
    await managerRef.current.signoutRedirect();
  }, []);

  return (
    <Ctx.Provider value={{ ready, enabled: config.enabled, user, signin, signout }}>
      {children}
    </Ctx.Provider>
  );
}
